import { useCallback } from "react";
import { useSnapshot } from "valtio";
import { refreshSiteGeometryStore } from "src/js/stores/siteGeometry/actions/refreshSiteGeometryStore";
import { mapStore } from "src/js/stores/map/store";
import { useOnlineStatus } from "react-migration/domains/nav/hooks/useOnlineStatus";
import { useSiteStages } from "../../hooks/useSiteStages";
import { SiteOverview } from "../../typings/Site";
import { SiteStage } from "../../typings/SiteStage";
import {
  SiteStageSelect as SiteStageSelectVisual,
  SiteStageSelectProps as SiteStageSelectVisualProps,
  SiteStageSelectrTriggerType,
} from "./SiteStageSelect";
import { showError } from "src/js/stores/user/actions/addNotification";
import { useUpdateSitesMutation } from "../SaveSiteButton/useUpdateSitesMutation/useUpdateSitesMutation";

type SiteStageSelectProps = Pick<SiteStageSelectVisualProps, "triggerType"> & {
  site?: SiteOverview;
  onStartedSaving?(): void;
  onCompleted?(): void;
  onError?(): void;
};

const EMPTY_SITE_STAGES: SiteStage[] = [];

export const SiteStageSelect = ({ site, triggerType, onStartedSaving, onCompleted, onError }: SiteStageSelectProps) => {
  const isOnline = useOnlineStatus();
  const { stages, loading: loadingStages } = useSiteStages();
  const [updateSites, { loading: saving }] = useUpdateSitesMutation();
  const { googleMap } = useSnapshot(mapStore);

  const onCompletedHandler = useCallback(() => {
    onCompleted?.();
    googleMap && refreshSiteGeometryStore(googleMap);
  }, [googleMap, onCompleted]);

  return (
    <SiteStageSelectVisual
      triggerType={triggerType}
      disabled={loadingStages || saving}
      onChange={(value) => {
        if (!site) return;

        if (!isOnline) {
          showError({
            id: "offline-save-stage-error",
            title: "Unable to perform action while offline.",
          });
          return;
        }

        onStartedSaving?.();

        updateSites({
          variables: { siteUpdates: { _id: site._id, _stage: value } },
          onCompleted: onCompletedHandler,
          onError: onError,
        });
      }}
      value={site?._stage?._id}
      stages={stages ?? EMPTY_SITE_STAGES}
    />
  );
};

SiteStageSelect.TriggerType = SiteStageSelectrTriggerType;
